<template>
  <ToolShell
    tool-name="Freelancer bio generator"
    h1="Free bio generator for freelancers, consultants and studios."
    lede="Answer five quick questions and get a short bio, a website about section, LinkedIn headlines and a mission statement you can edit and copy. Free, no signup, and it runs in your browser."
    :trust="['No signup', 'First or third person', 'LinkedIn headlines']"
    form-title="About your work"
    :upsell-points="[
      'Proposals and invoices carry your business name',
      'Clients approve and pay online from a link',
      'Every client’s messages, files and payments in one record',
    ]"
    free-label="The free generator"
    :free-bullets="[
      'A bio and headline to paste where clients look',
      'You keep each profile up to date by hand',
      'Inquiries still land in scattered inboxes',
    ]"
    upgrade-headline="Your bio wins the first conversation. Workspace369 runs the client work that follows."
    :connected-bullets="[
      'Inquiries, email and texts on one client record',
      'Proposals clients approve online, then invoices from the same record',
      'A client portal for files, invoices and messages',
      'A 14-day free trial, then plans from $29 a month',
    ]"
    slug="/tools/freelancer-bio-generator"
    :meta-description="metaDescription"
    :doc-brand="form.name || sample.name"
    :doc-subtitle="docSubtitle"
    doc-kind="BIO"
    stamp="Draft"
  >
    <template #form>
      <div class="ta-field-row">
        <label class="ta-field">
          <span>Your name</span>
          <div class="ta-input">
            <input v-model="form.name" type="text" :placeholder="sample.name" autocomplete="name" maxlength="60" />
          </div>
        </label>
        <label class="ta-field">
          <span>What you do</span>
          <div class="ta-input">
            <input v-model="form.role" type="text" :placeholder="sample.role" autocomplete="organization-title" maxlength="60" />
          </div>
        </label>
      </div>

      <label class="ta-field">
        <span>Who you help</span>
        <div class="ta-input">
          <input v-model="form.audience" type="text" :placeholder="sample.audience" maxlength="80" />
        </div>
      </label>

      <label class="ta-field">
        <span>The result you help them get</span>
        <div class="ta-input">
          <input v-model="form.outcome" type="text" :placeholder="sample.outcome" maxlength="100" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Years of experience</span>
          <div class="ta-input">
            <input v-model.number="form.years" type="number" min="0" max="60" inputmode="numeric" :placeholder="String(sample.years)" />
          </div>
        </label>
        <label class="ta-field">
          <span>City (optional)</span>
          <div class="ta-input">
            <input v-model="form.location" type="text" placeholder="Austin, TX" maxlength="60" />
          </div>
        </label>
      </div>

      <label class="ta-field">
        <span>Proof: a client, result or credential (optional)</span>
        <div class="ta-input">
          <input v-model="form.proof" type="text" :placeholder="sample.proof" maxlength="140" />
        </div>
      </label>

      <label class="ta-field">
        <span>Something personal (optional)</span>
        <div class="ta-input">
          <input v-model="form.personal" type="text" placeholder="I teach pottery on weekends" maxlength="120" />
        </div>
      </label>

      <div class="ta-field-row">
        <div class="ta-field">
          <span id="bio-tone-label">Tone</span>
          <ToolDropdown v-model="form.tone" :options="toneOptions" aria-label="Tone" labelledby="bio-tone-label" />
        </div>
        <div class="ta-field">
          <span id="bio-person-label">Voice</span>
          <ToolDropdown v-model="form.person" :options="personOptions" aria-label="Voice" labelledby="bio-person-label" />
        </div>
      </div>

      <button type="button" class="ta-download" @click="seed += 1">New versions</button>
      <button type="button" class="ta-download" @click="copy('all', allText)">
        {{ copied === "all" ? "Copied!" : "Copy everything" }}
      </button>

      <p class="ta-note">
        These are starting drafts. Read them out loud, swap in your own words, and only claim
        results you can back up.
      </p>
    </template>

    <template #preview>
      <div class="bio-blocks">
        <section v-for="block in blocks" :key="block.key" class="bio-block">
          <header>
            <h3 :id="`bio-heading-${block.key}`">{{ block.title }}</h3>
            <span class="bio-meta">{{ block.meta }}</span>
            <button type="button" class="bio-copy" :aria-labelledby="`bio-copy-${block.key} bio-heading-${block.key}`" @click="copy(block.key, block.text)">
              <span :id="`bio-copy-${block.key}`">{{ copied === block.key ? "Copied" : "Copy" }}</span>
            </button>
          </header>
          <ul v-if="block.list" class="bio-list" translate="no">
            <li v-for="line in block.list" :key="line">{{ line }}</li>
          </ul>
          <p v-else translate="no">{{ block.text }}</p>
        </section>
      </div>
      <p class="ta-doc-note">
        {{ usingSample ? "Showing a sample until you fill in what you do and who you help." : "Drafts update as you type. Nothing you enter leaves your browser." }}
      </p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Describe your work</h2><p>What you do, who you help and the result they get. Add proof and a personal detail if you have them.</p></div></div>
      <div><span>02</span><div><h2>Pick tone and voice</h2><p>Professional, friendly or bold; first person for your site, third person for speaker and guest bios.</p></div></div>
      <div><span>03</span><div><h2>Edit and paste</h2><p>Copy the version that fits: a short bio for social profiles, the longer one for your about page.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Freelancer bio FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/freelancer-bio-generator" title="Freelancer bio generator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { generateBios, hasEnoughForBio, wordCount } from "~/utils/freelancer-bio.mjs";

const sample = {
  name: "Alex Rivera",
  role: "Brand designer",
  audience: "wellness startups",
  outcome: "look established from day one",
  years: 8,
  proof: "rebrands for 40 independent studios",
};

const form = reactive({
  name: "",
  role: "",
  audience: "",
  outcome: "",
  years: "",
  location: "",
  proof: "",
  personal: "",
  tone: "professional",
  person: "first",
});

const toneOptions = [
  { value: "professional", label: "Professional", hint: "calm and credible" },
  { value: "friendly", label: "Friendly", hint: "warm and plain-spoken" },
  { value: "bold", label: "Bold", hint: "short and confident" },
];
const personOptions = [
  { value: "first", label: "First person", hint: "“I help…”" },
  { value: "third", label: "Third person", hint: "“Alex helps…”" },
];

const seed = ref(0);
watch(() => ({ ...form }), () => { seed.value = 0; }, { deep: true });

const usingSample = computed(() => !hasEnoughForBio(form));
const details = computed(() => (usingSample.value ? { ...sample, tone: form.tone, person: form.person } : { ...form }));
const bios = computed(() => generateBios(details.value, seed.value));

const words = (text) => `${wordCount(text)} words`;
const blocks = computed(() => {
  const b = bios.value;
  if (!b) return [];
  return [
    { key: "short", title: "Short bio", meta: `${words(b.short)} · social profiles`, text: b.short },
    { key: "medium", title: "Website bio", meta: `${words(b.medium)} · about page`, text: b.medium },
    { key: "long", title: "Long bio", meta: `${words(b.long)} · proposals and guest posts`, text: b.long },
    { key: "headlines", title: "LinkedIn headlines", meta: "pick one", list: b.headlines, text: b.headlines.join("\n") },
    { key: "mission", title: "Mission statement", meta: "two options", list: b.mission, text: b.mission.join("\n") },
  ];
});

const allText = computed(() => blocks.value.map((block) => `${block.title}\n${block.text}`).join("\n\n"));
const docSubtitle = computed(() => (form.role || sample.role));

const copied = ref("");
const copy = async (key, text) => {
  try {
    await navigator.clipboard.writeText(typeof text === "string" ? text : text.value);
    copied.value = key;
    setTimeout(() => (copied.value = ""), 1600);
  } catch { /* clipboard unavailable */ }
};

const faq = [
  {
    q: "What should a freelancer bio include?",
    a: "What you do, who you do it for, and the result they get, in the first sentence. Then one piece of proof (a client, a result or a credential), how you work, and a line that invites the next step. A personal detail is optional but makes the bio easier to remember.",
  },
  {
    q: "Should a bio be in first or third person?",
    a: "Use first person on your own website, LinkedIn and proposals: it reads as a conversation with the client. Use third person when someone else introduces you, such as a conference program, a podcast page or a guest post.",
  },
  {
    q: "How long should a freelancer bio be?",
    a: "One or two sentences (about 30 to 50 words) for social profiles and directories, and 80 to 150 words for an about page or proposal. If a sentence doesn’t help a client decide to contact you, cut it.",
  },
  {
    q: "What makes a good LinkedIn headline for a freelancer?",
    a: "Say who you help and what changes for them, not just your job title. “Brand designer for wellness startups | I help you look established from day one” tells a prospect in one line whether you are for them.",
  },
  {
    q: "Do I need a mission statement as a freelancer?",
    a: "Not formally, but writing one sentence about who you serve and why helps you say no to work that doesn’t fit and keeps your bio, website and proposals consistent. Keep it short enough to remember.",
  },
  {
    q: "Is what I type stored?",
    a: "No. The drafts are built in your browser from what you type, and nothing is sent to Workspace369 or saved after you close the page.",
  },
];

const metaTitle = "Free Freelancer Bio & LinkedIn Headline Tool | Workspace369";
const metaDescription =
  "Free bio generator for freelancers and consultants: get a short bio, website about section, LinkedIn headlines and a mission statement in seconds. No signup.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.bio-blocks {
  display: grid;
  gap: 0.75rem;
}
.bio-block {
  padding: 0.75rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #fff;
}
.bio-block header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 0.6rem;
  margin-bottom: 0.4rem;
}
.bio-block h3 {
  margin: 0;
  color: #111827;
  font-size: 0.9rem;
  font-weight: 700;
}
.bio-meta {
  color: #6b7280;
  font-size: 0.75rem;
}
.bio-copy {
  margin-left: auto;
  padding: 0.15rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #0e7490;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.bio-copy:hover {
  border-color: #0e7490;
}
.bio-block p,
.bio-list {
  margin: 0;
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.55;
}
.bio-list {
  display: grid;
  gap: 0.3rem;
  padding-left: 1.1rem;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .bio-block {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .bio-block h3 {
  color: #f1f5f9;
}

.dark .bio-meta {
  color: #cbd5e1;
}

.dark .bio-copy {
  border: 1px solid #243247;
  background: #0f172a;
  color: #67e8f9;
}

.dark .bio-block p,
.dark .bio-list {
  color: #cbd5e1;
}


</style>
